'use client'

import { twMerge } from '@/lib/classname/twMerge'
import type { WebViewIntent } from '@/lib/react-native/services/react-native.intent'
import type { TrackingGaEvent } from '@/lib/tracking/services/tracking.eventTypes'
import { Card } from '@/ui/cards/card/Card'
import { Link } from '@/ui/links/link/Link.client'
import { CATEGORY_IMAGE_SIZE_CARD } from '@/ui/media/cloudflare-picture-category/CloudflarePictureCategory'
import { CloudflarePicture } from '@/ui/media/cloudflare-picture/CloudflarePicture.client'
import { Picture } from '@/ui/media/picture/Picture.client'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import { type Image } from '@gql-generated/graphql'

const PICTURE_CLASSNAME = 'h-[5.75rem] w-[9.125rem] shrink-0'
const PICTURE_MODE = 'contain' as const

export type CategoryNavigationCardProps = UiComponentProps<
  WithPayloadBlockId<{
    image?: Image
    href: string
    children: React.ReactNode
    imageSrc?: string
    trackingData?: TrackingGaEvent
    intent?: WebViewIntent
    opensInNewTab?: boolean
  }>
>

export function CategoryNavigationCard({
  image,
  href,
  children,
  imageSrc,
  trackingData,
  intent,
  opensInNewTab,
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'data-payload-block-id': dataPayloadBlockId,
}: CategoryNavigationCardProps) {
  return (
    <Link
      variant="free"
      underlined="none"
      href={href}
      className={className}
      trackingData={trackingData}
      intent={intent}
      opensInNewTab={opensInNewTab}
      id={id}
      aria-label={ariaLabel}
      data-testid={dataTestId ?? 'category-navigation-card'}
      data-payload-block-id={dataPayloadBlockId}
    >
      <Card
        radius="sm"
        skin="body-alt"
        className="h-15 w-full items-center gap-2 font-bold leading-light desktop:gap-1"
      >
        {image && typeof image === 'object' ? (
          <Picture className={PICTURE_CLASSNAME} image={image} mode={PICTURE_MODE} />
        ) : (
          <CloudflarePicture
            src={imageSrc}
            alt=""
            width={CATEGORY_IMAGE_SIZE_CARD}
            className={PICTURE_CLASSNAME}
            mode={PICTURE_MODE}
            isInternalPicture
          />
        )}
        <div className="flex w-full flex-col justify-center overflow-hidden break-words">
          {children}
        </div>
      </Card>
    </Link>
  )
}

export function CategoryNavigationCardSkeleton({
  className,
  id,
  'data-testid': dataTestId,
}: UiComponentProps) {
  return (
    <Card
      id={id}
      radius="sm"
      skin="body-alt"
      aria-hidden
      data-testid={dataTestId ?? 'category-navigation-card-skeleton'}
      className={twMerge(className, 'h-15 w-full items-center gap-2')}
    >
      <BaseSkeleton className="size-10 shrink-0" />
      <BaseSkeleton className="h-[0.875rem] w-[6.25rem]" />
    </Card>
  )
}
